/**
 * Utilitaires pour le module Operation
 * Fonctions de transformation et helpers
 */

import { ApiOperation, ApiStatus, Operation } from "./types";
import {
  STATUS_ACTION_MAP_BC,
  STATUS_ACTION_MAP_MS,
  STATUS_ACTION_MAP_BCI,
  StatusActionMap,
} from "./statusConfig";

// ============================================================================
// Fonctions de formatage
// ============================================================================

/**
 * Formate une date ISO en format français (JJ/MM/AAAA)
 */
export const formatDateFR = (isoDate: string | null | undefined): string => {
  if (!isoDate) return "";
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString("fr-FR");
  } catch {
    return "";
  }
};

/**
 * Formate un prix en euros
 */
export const formatPriceEUR = (price: number): string => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(price);
};

/**
 * Convertit une chaîne en classe CSS valide
 */
export const toCssClass = (str: string): string => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Supprime les accents
    .replace(/\s+/g, "-") // Remplace les espaces par des tirets
    .replace(/[^a-zA-Z0-9-]/g, "") // Supprime les caractères non alphanumériques
    .toLowerCase();
};

// ============================================================================
// Fonctions de transformation des données API
// ============================================================================

/**
 * Crée un objet de statut par défaut
 */
const createDefaultStatus = (): ApiStatus => ({
  id: 0,
  nom: "inconnu",
  action: false,
  delai: null,
  type_compte: "",
});

/**
 * Formate l'affichage du lot technique
 */
const formatLotTechniqueDisplay = (
  lotTechnique: ApiOperation["lot_technique"]
): string => {
  if (!lotTechnique) return "Non spécifié";
  if (lotTechnique.id) {
    return `${lotTechnique.id} - ${lotTechnique.nom}`;
  }
  return lotTechnique.nom || "Non spécifié";
};

/**
 * Transforme une opération de l'API vers le format d'affichage
 */
export const transformApiOperationToDisplay = (
  apiOp: ApiOperation
): Operation => {
  const status: ApiStatus = apiOp.status ?? createDefaultStatus();

  return {
    // Identifiants
    id: apiOp.id.toString(),
    numero: apiOp.numero || "",

    // Informations générales
    nom: apiOp.nom || "",
    source: apiOp.source,
    creePar: apiOp.cree_par || "Inconnu",

    // Dates
    dateCreation: formatDateFR(apiOp.created_at),
    expireAt: apiOp.expire_at || "",

    // Lots
    lotTechniqueId: apiOp.lot_technique?.id ?? null,
    lotTechniqueNom: apiOp.lot_technique?.nom || "",
    lotTechniqueDisplay: formatLotTechniqueDisplay(apiOp.lot_technique),
    departement: apiOp.id_lot_departement?.toString() || "Non spécifié",

    // Entités
    organismeNom: apiOp.organisme_nom || "Non défini",
    prestataireNom: apiOp.prestataire?.entite?.nom || "Non défini",

    // Montants
    prixTotal: apiOp.prix_total ? parseFloat(apiOp.prix_total) : 0,

    // Statut
    status,
    actionRequise: status.action === true,

    // Notes
    noteTotal: apiOp.note_total,
    noteTechnique: apiOp.id_note_technique,
    noteFinanciere: apiOp.note_financiere,

    // Autres
    commentaire: apiOp.commentaire,
    visite: apiOp.visite,
    reponseTechnique: apiOp.reponse_technique,
  };
};

/**
 * Transforme un tableau d'opérations de l'API vers le format d'affichage
 */
export const transformApiOperationsToDisplay = (
  apiOperations: ApiOperation[]
): Operation[] => {
  return apiOperations.map(transformApiOperationToDisplay);
};

/**
 * Transforme une opération de panier importé (BCI) vers le format d'affichage
 */
export const transformBciOperationToDisplay = (apiOp: any): Operation => {
  const status: ApiStatus = apiOp.status ?? createDefaultStatus();

  return {
    // Identifiants
    id: apiOp.id?.toString() || "",
    numero: apiOp.numero || `BCI-${apiOp.id}`,

    // Informations générales
    nom: apiOp.reference || apiOp.nom || "",
    source: "BCI",
    creePar: "", // Non applicable pour les paniers importés

    // Dates
    dateCreation: formatDateFR(apiOp.date_debut_operation || apiOp.created_at),
    expireAt: apiOp.expire_at || "",

    // Lots
    lotTechniqueId: apiOp.lot_technique?.id ?? null,
    lotTechniqueNom: apiOp.lot_technique?.nom || "",
    lotTechniqueDisplay: formatLotTechniqueDisplay(apiOp.lot_technique),
    departement: apiOp.id_lot_departement?.toString() || "Non spécifié",

    // Entités
    organismeNom: apiOp.organisme_nom || "Non défini",
    prestataireNom: "Non défini", // Non applicable pour les paniers importés

    // Montants
    prixTotal: apiOp.prix_total ? parseFloat(apiOp.prix_total) : 0,

    // Statut
    status,
    actionRequise: status.action === true,

    // Notes
    noteTotal: apiOp.note_total,
    noteTechnique: apiOp.id_note_technique,
    noteFinanciere: apiOp.note_financiere,

    // Autres
    commentaire: apiOp.commentaire,
    visite: apiOp.visite,
    reponseTechnique: apiOp.reponse_technique,
  };
};

// ============================================================================
// Fonctions pour les actions de statut
// ============================================================================

/**
 * Retourne le mapping d'actions approprié selon le type d'opération
 */
const getStatusActionMap = (source: "BC" | "MS" | "BCI"): StatusActionMap => {
  switch (source) {
    case "BC":
      return STATUS_ACTION_MAP_BC;
    case "BCI":
      return STATUS_ACTION_MAP_BCI;
    case "MS":
    default:
      return STATUS_ACTION_MAP_MS;
  }
};

/**
 * Détermine l'action à afficher pour un statut donné et un rôle utilisateur
 */
export const getStatusActionForRole = (
  statusId: number,
  userRole: string,
  source: "BC" | "MS" | "BCI"
): {
  buttonText: string;
  action: string;
  showButton: boolean;
  disableButton: boolean;
} => {
  const statusActionMap = getStatusActionMap(source);
  const mapping = statusActionMap[statusId];

  if (!mapping) {
    return {
      buttonText: "",
      action: "",
      showButton: false,
      disableButton: true,
    };
  }

  // Vérifie si le rôle utilisateur est autorisé
  const isRoleAuthorized =
    mapping.forRoles.some((role) =>
      userRole.toLowerCase().includes(role.toLowerCase())
    ) || mapping.forRoles.includes("*");

  const showButton = mapping.buttonText !== "" && isRoleAuthorized;
  const disableButton = mapping.buttonText === "";

  return {
    buttonText: mapping.buttonText,
    action: mapping.action,
    showButton,
    disableButton,
  };
};

/**
 * Récupère le rôle utilisateur depuis le localStorage
 */
export const getUserRoleFromStorage = (): string => {
  try {
    const userJson = localStorage.getItem("user");
    if (userJson) {
      const userData = JSON.parse(userJson);
      return userData.role || "";
    }
  } catch (error) {
    console.error("Erreur lors de la récupération du rôle utilisateur:", error);
  }
  return "";
};

// ============================================================================
// Fonctions de tri
// ============================================================================

/**
 * Trie les opérations : actions requises en premier, puis par date décroissante
 */
export const sortOperationsByPriority = (
  operations: Operation[]
): Operation[] => {
  return [...operations].sort((a, b) => {
    // Priorité 1 : Action requise en premier
    if (a.actionRequise && !b.actionRequise) return -1;
    if (!a.actionRequise && b.actionRequise) return 1;

    // Priorité 2 : Par date de création (plus récent en premier)
    const dateA = new Date(a.expireAt || a.dateCreation).getTime();
    const dateB = new Date(b.expireAt || b.dateCreation).getTime();
    return dateB - dateA;
  });
};

/**
 * Trie les paniers importés : À accepter en premier (status.id === 4)
 */
export const sortPaniersImportesByPriority = (
  operations: Operation[]
): Operation[] => {
  return [...operations].sort((a, b) => {
    const aIsAAccepter = a.status.id === 4 && a.status.action === true;
    const bIsAAccepter = b.status.id === 4 && b.status.action === true;

    if (aIsAAccepter && !bIsAAccepter) return -1;
    if (!aIsAAccepter && bIsAAccepter) return 1;

    // Par date décroissante
    const dateA = new Date(a.dateCreation).getTime();
    const dateB = new Date(b.dateCreation).getTime();
    return dateB - dateA;
  });
};
